import { Plus } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { useState } from "react";
import { PageLayout } from "@/components/layout";

const faqs = [
	{
		category: "General",
		questions: [
			{
				q: "What is Basilica?",
				a: "Basilica is public digital infrastructure — a decentralized platform where applications are owned by their users, not corporations. Think of it as a foundation layer for internet services that can't be shut down, acquired, or enshittified.",
			},
			{
				q: "How is this different from blockchain?",
				a: "Most blockchains optimize for financial transactions. Basilica optimizes for applications. Our consensus mechanism (proof of PageRank) rewards useful contributions to the network rather than capital expenditure. The result is infrastructure that behaves more like the web and less like a ledger.",
			},
			{
				q: "Who is Basilica for?",
				a: "Developers who want to build applications that survive independently of any single company. Creators who want to own their distribution. Anyone who thinks the internet should have public infrastructure the same way cities have public parks.",
			},
			{
				q: "Is Basilica open source?",
				a: "Yes. The core protocol, client software, and developer tools are all open source. We believe public infrastructure should be publicly auditable. Anyone can inspect the code, contribute improvements, or fork it entirely.",
			},
			{
				q: "Do I need to understand cryptography or distributed systems to get started?",
				a: "No. Basilica abstracts the hard parts away. If you've built a web app with React, Next.js, or a similar framework, the development experience will feel familiar. The CLI handles networking, replication, and consensus — you just write your application logic.",
			},
		],
	},
	{
		category: "Technology",
		questions: [
			{
				q: "What is proof of PageRank?",
				a: "It's our novel consensus mechanism inspired by Google's original PageRank algorithm. Instead of proving you spent electricity (proof of work) or locked up capital (proof of stake), you prove you're a valuable participant in the network. Quality surfaces through mathematics, not money.",
			},
			{
				q: "What can I build on Basilica?",
				a: "Anything you'd build as a web application — social networks, marketplaces, productivity tools, content platforms, games. The difference is your application runs on shared public infrastructure and can't be taken away from its users.",
			},
			{
				q: "How do applications handle data storage?",
				a: "Basilica provides a distributed content layer where data is replicated across the network. Applications store data with content-addressed identifiers, and the network handles replication, availability, and persistence automatically.",
			},
			{
				q: "What should I think about differently when building on Basilica?",
				a: "The biggest shift is ownership. On traditional platforms, you're renting space — the platform can change terms, throttle your app, or shut you down. On Basilica, your application is a first-class citizen of the network. Design for user sovereignty: let users own their data, let communities govern their spaces, and build features that get better as more people participate rather than locking them in.",
			},
			{
				q: "What does the developer workflow look like?",
				a: "Install the Basilica CLI, run `basilica init` to scaffold a project, and develop locally with hot reloading like any modern web framework. When you're ready, `basilica deploy` publishes your app to the network. There's no infrastructure to provision, no servers to manage, no deploy pipelines to configure.",
			},
		],
	},
	{
		category: "Access & Economics",
		questions: [
			{
				q: "How do I get access?",
				a: "We're currently in early access. Sign up on our homepage and we'll reach out as we expand. We're onboarding developers and teams in batches to ensure the network scales responsibly.",
			},
			{
				q: "Is it free?",
				a: "Running applications on Basilica is free up to a generous baseline. The network sustains itself through block rewards distributed to participants. There are no platform fees, no rent-seeking middlemen.",
			},
			{
				q: "How do creators earn money?",
				a: "Block rewards are distributed to all network participants, including application developers. If you build something people use, you earn revenue from day one. 100% of creator rewards go to creators — we don't take a cut.",
			},
			{
				q: "Can I migrate an existing application to Basilica?",
				a: "Yes. If your app is built with standard web technologies, migration is straightforward. Our CLI tools can scaffold a Basilica project from an existing codebase, and we provide migration guides for common frameworks. Most teams are up and running within a day.",
			},
			{
				q: "What kind of apps work best on Basilica today?",
				a: "Apps with strong community dynamics — social platforms, collaborative tools, creator marketplaces, forums, and anything where users benefit from owning their data and relationships. These are also the apps most harmed by traditional platforms, so the value proposition is immediate.",
			},
		],
	},
];

function FAQItem({ question, answer, index }: { question: string; answer: string; index: number }) {
	const [open, setOpen] = useState(false);

	return (
		<motion.div
			initial={{ opacity: 0, y: 12 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.4, delay: index * 0.04 }}
			className="border-t border-white/[0.06]"
		>
			<button
				onClick={() => setOpen(!open)}
				className="w-full py-6 md:py-7 flex items-start justify-between gap-6 text-left cursor-pointer"
			>
				<span className="text-[17px] md:text-[18px] text-white font-normal tracking-tight leading-snug">{question}</span>
				<motion.span
					animate={{ rotate: open ? 45 : 0 }}
					transition={{ duration: 0.2 }}
					className="mt-1 text-white/30 shrink-0"
				>
					<Plus size={18} weight="bold" />
				</motion.span>
			</button>
			<AnimatePresence>
				{open && (
					<motion.div
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: "auto", opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={{ duration: 0.25, ease: [0.25, 1, 0.5, 1] }}
						className="overflow-hidden"
					>
						<p className="pb-7 text-[15px] md:text-[16px] text-white/40 leading-relaxed max-w-2xl">
							{answer}
						</p>
					</motion.div>
				)}
			</AnimatePresence>
		</motion.div>
	);
}

export default function FAQsRoute() {
	return (
		<PageLayout activeRoute="/faqs">
			{/* Hero */}
			<section className="pt-20 md:pt-32 pb-16 md:pb-20 px-9 md:px-[8%] lg:px-[15%]">
				<motion.p
					initial={{ opacity: 0, y: 12 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4 }}
					className="text-[14px] uppercase tracking-[0.2em] text-white/30 mb-6"
				>
					FAQs
				</motion.p>
				<motion.h1
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0.05 }}
					className="text-[36px] md:text-[56px] lg:text-[72px] font-normal tracking-tight text-white leading-[1.05] max-w-3xl"
				>
					Questions we<br />
					<span className="text-white/40">get asked a lot.</span>
				</motion.h1>
			</section>

			{/* FAQ sections */}
			{faqs.map((section, si) => (
				<section key={section.category} className="px-9 md:px-[8%] lg:px-[15%] pb-16 md:pb-20">
					<motion.p
						initial={{ opacity: 0, y: 12 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						className="text-[14px] uppercase tracking-[0.2em] text-white/30 mb-8"
					>
						{section.category}
					</motion.p>
					<div>
						{section.questions.map((faq, qi) => (
							<FAQItem
								key={faq.q}
								question={faq.q}
								answer={faq.a}
								index={si * 3 + qi}
							/>
						))}
					</div>
				</section>
			))}

			{/* CTA */}
			<section className="px-9 md:px-[8%] lg:px-[15%] pb-24 md:pb-32">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-10 md:p-14 text-center"
				>
					<h2 className="text-[24px] md:text-[32px] font-normal tracking-tight text-white mb-4">Still have questions?</h2>
					<p className="text-[17px] text-white/40 mb-8">Reach out to us directly. We read every message.</p>
					<a
						href="mailto:hello@basilica.dev"
						className="inline-flex items-center gap-2 text-[15px] text-white/60 hover:text-white transition-colors px-6 py-3 rounded-xl bg-white/[0.06] hover:bg-white/10"
					>
						hello@basilica.dev
					</a>
				</motion.div>
			</section>
		</PageLayout>
	);
}
